import React, { useMemo } from 'react'
import type { ViewProps } from 'react-native'
import { View } from 'react-native'
import type { MixedStyleDeclaration, RenderHTMLProps } from 'react-native-render-html'
import HTML from 'react-native-render-html'
import { StyleSheet } from 'react-native-unistyles'
import { tv } from 'tailwind-variants'
import { match } from 'ts-pattern'

import { theme as uiKitTheme } from '@ui-kit/theme'

const htmlRenderer = tv({
  slots: {
    container: 'flex-shrink',
  },
})

export const body2BaseStyle = {
  fontFamily: uiKitTheme.fontFamily.interRegular,
  fontSize: uiKitTheme.fontSizes.sm,
  lineHeight: 24,
} as const

export const HTMLRenderer = (
  props: RenderHTMLProps &
    ViewProps & {
      className?: string
      textCenter?: boolean
      textSmall?: boolean
    }
) => {
  const renderersProps = useMemo(() => {
    return {
      ...props.renderersProps,
      ul: {
        markerBoxStyle: styles.markerBoxStyle,
        markerTextStyle: styles.markerTextStyle,
        ...props.renderersProps?.ul,
      },
    }
  }, [props.renderersProps])

  const mergedTagsStyles = useMemo(
    () => ({
      ...(tagsStyles as Record<string, MixedStyleDeclaration>),
      ...props.tagsStyles,
    }),
    [props.tagsStyles]
  )

  const { container } = htmlRenderer()

  return (
    <View className={container()} style={props.style}>
      <HTML
        contentWidth={props.contentWidth}
        source={props.source}
        renderersProps={renderersProps}
        baseStyle={{
          ...(styles.baseStyles({
            textCenter: props.textCenter,
            textSmall: props.textSmall,
          }) as MixedStyleDeclaration),
          ...props.baseStyle,
        }}
        tagsStyles={mergedTagsStyles}
        classesStyles={props.classesStyles}
      />
    </View>
  )
}

const styles = StyleSheet.create((theme) => ({
  baseStyles: (options: { textCenter?: boolean; textSmall?: boolean }) => {
    const commonStyles = {
      color: theme.colors.black,
      fontFamily: theme.fontFamily.modernEraRegular,
      textAlign: options.textCenter ? 'center' : 'left',
    } as const
    return match(options)
      .with({ textSmall: true }, () => ({
        ...commonStyles,
        fontSize: {
          XS: theme.fontSizes.xs,
          L: theme.fontSizes.sm,
        },
        lineHeight: {
          XS: theme.lineHeights[5],
          L: theme.lineHeights[6],
        },
      }))
      .otherwise(() => ({
        ...commonStyles,
        fontSize: {
          XS: theme.fontSizes.sm,
          M: theme.fontSizes.base,
        },
        lineHeight: {
          XS: theme.lineHeights[7],
          L: theme.lineHeights[8],
        },
      }))
  },
  markerBoxStyle: {
    height: theme.space.mini,
    borderRadius: theme.space.xsmall,
    backgroundColor: theme.colors.deepForest,
    alignSelf: 'center',
  },
  markerTextStyle: {
    height: 0,
    backgroundColor: 'transparent',
    display: 'none',
  },
}))

const tagsStyles = StyleSheet.create((theme) => ({
  p: {
    marginTop: 0,
  },
  b: {
    fontWeight: 'bold',
    fontFamily: theme.fontFamily.modernEraMedium,
  },
  a: {
    color: theme.colors.deepForest,
    textDecorationLine: 'underline',
  },
  ul: {
    marginBottom: 0,
    paddingLeft: theme.space.mini,
    listStyleType: 'none',
  },
  li: {
    paddingLeft: theme.space.xxsmall,
  },
  missedappointment: {
    color: theme.colors.error,
    textDecorationLine: 'line-through',
    fontWeight: 'bold',
  },
  h1: {
    fontSize: theme.fontSizes['2xl'],
    lineHeight: theme.lineHeights[10],
    marginBottom: theme.space.medium,
    fontFamily: theme.fontFamily.modernEraMedium,
  },
  h2: {
    fontSize: theme.fontSizes.lg,
    lineHeight: theme.lineHeights[8],
    marginBottom: theme.space.small,
    fontFamily: theme.fontFamily.modernEraMedium,
  },
  h3: {
    fontSize: theme.fontSizes.base,
    lineHeight: theme.lineHeights[8],
    marginBottom: theme.space.small,
    fontFamily: theme.fontFamily.modernEraMedium,
  },
  h4: {
    marginBottom: theme.space.small,
    fontFamily: theme.fontFamily.modernEraMedium,
  },
  h5: {
    marginBottom: theme.space.xxsmall,
    fontFamily: theme.fontFamily.modernEraMedium,
  },
}))
